import React, { useState, useEffect } from 'react';
import { useGameTypes, GameType } from '../../hooks/useGameTypes';
import { useNavigate } from 'react-router-dom';
import GameConfirmationModal from '../GameConfirmationModal/GameConfirmationModal';
import './FeaturedGames.css';

// Map numeric levels to backend difficulty levels
const DIFFICULTY_MAP: Record<string, string> = {
  '1': 'easy',
  '2': 'medium',
  '3': 'hard'
};

// Define color pairs for each game type
const GAME_COLORS: Record<string, [string, string]> = {
  'apocalypse': ['#890101', '#ff4d4d'],
  'detective': ['#2c0152', '#9b4dff'],
  'space_explorer': ['#013359', '#4d79ff'],
  'hitler_assassination': ['green', 'black'],
  'fairy_realm': ['yellow', 'black'],
  'default': ['#890101', '#ff4d4d'] // Fallback colors
};

interface GameWithKey extends GameType {
  key: string;
}

const FeaturedGames: React.FC = () => {
  const navigate = useNavigate();
  const { gameTypes, isLoading, error } = useGameTypes();
  const [selectedGame, setSelectedGame] = useState<string | null>(null);
  const [selectedDifficulty, setSelectedDifficulty] = useState<string>('2'); // Default to medium (2)
  const [isModalOpen, setIsModalOpen] = useState(false);

  useEffect(() => {
    if (gameTypes && !selectedGame) {
      const availableGames = Object.entries(gameTypes)
        .filter(([, gameData]) => gameData.availability === 1)
        .map(([key]) => key);
      
      if (availableGames.length > 0) {
        setSelectedGame(availableGames[0]);
      }
    }
  }, [gameTypes, selectedGame]);

  // Early return if still loading
  if (isLoading || !gameTypes) {
    return <div className="loading">Loading games...</div>;
  }

  if (error) {
    return <div className="error">{error}</div>;
  }

  // Convert gameTypes object to array and filter out unavailable games
  const gamesList: GameWithKey[] = Object.entries(gameTypes)
    .filter(([, gameData]) => gameData.availability === 1)
    .map(([key, game]) => ({ key, ...game }));

  const featuredGame = selectedGame ? gameTypes[selectedGame] : null;
  const miniGames = gamesList
    .filter(game => game.key !== selectedGame)
    .slice(0, 4); // Show up to 4 mini cards

  const handleMiniCardClick = (gameKey: string) => {
    setSelectedGame(gameKey);
    setSelectedDifficulty('2'); // Reset difficulty to medium (2) when switching games
  };

  const handleDifficultyChange = (level: string) => {
    setSelectedDifficulty(level);
  };

  const handlePlayClick = () => {
    if (!selectedGame) return;
    setIsModalOpen(true);
  };

  const handleModalClose = () => {
    setIsModalOpen(false);
  };

  const handleModalConfirm = () => {
    if (!selectedGame) return;
    setIsModalOpen(false);
    navigate(`/game/${selectedGame}`, {
      state: {
        difficulty: DIFFICULTY_MAP[selectedDifficulty],
        gameType: selectedGame
      }
    });
  };

  if (!featuredGame) {
    return <div className="error">No games available</div>;
  }

  // Get colors for the selected game
  const [color1, color2] = selectedGame ? (GAME_COLORS[selectedGame] || GAME_COLORS.default) : GAME_COLORS.default;

  return (
    <div className="game-cards">
      <div 
        className={`game-card active maingamecard ${selectedGame}`}
        style={{
          '--game-gradient-start': color1,
          '--game-gradient-end': color2,
        } as React.CSSProperties}
      >
        <div className="game-info">
          <div className="platform">{featuredGame.name}</div>
          <div className="genre-platform">
            <div className="genre">
              <span>Game Genre</span>
              <h4>{featuredGame.genre}</h4>
            </div>
            <div className="divider"></div>
            <div className="platform-info">
              <span>Platform</span>
              <i className="material-icons">windows</i>
            </div>
          </div>
          <div className="play-more">
            <div className="price-card">Select Difficulty</div>
            <div className="difficulty-options">
              {['1', '2', '3'].map((level) => (
                <label key={level}>
                  <input
                    type="radio"
                    name="difficulty"
                    value={level}
                    checked={selectedDifficulty === level}
                    onChange={() => handleDifficultyChange(level)}
                  />
                  <span>{level}</span>
                </label>
              ))}
            </div>
          </div>
        </div>
        <div className="game-card sub-card">
          {featuredGame.intro_video_url ? (
            <video
              key={selectedGame}
              className="game-video-bg"
              autoPlay
              loop
              muted
              playsInline
              poster={featuredGame.image_url || "/e.png"}
              onError={(e) => {
                const target = e.target as HTMLVideoElement;
                target.style.display = 'none';
              }}
            >
              <source src={featuredGame.intro_video_url} type="video/mp4" />
              <source src={featuredGame.intro_video_url} type="video/webm" />
              Your browser does not support the video tag.
            </video>
          ) : (
            <img 
              key={selectedGame}
              src={featuredGame.image_url || "/e.png"} 
              alt={featuredGame.name} 
              className="game-video-bg" 
            />
          )}
          <div className="game-info">
            <h3>{selectedGame ? selectedGame.replace(/_/g, ' ').replace(/\b\w/g, (char: string) => char.toUpperCase()) : ''}</h3>
            <p>{featuredGame.short_description}</p>
            <div className="rating-price-wrapper">
              <div className="rating">{featuredGame.rating}</div>
              <button className="play-btn" onClick={handlePlayClick}>
                Play <i className="fas fa-play"></i>
              </button>
            </div>
            <div className="age-rating">6+</div>
          </div>
        </div>
      </div>

      <div className="mini-cards">
        {miniGames.map((game) => (
          <div 
            key={game.key}
            className="mini-game-card"
            onClick={() => handleMiniCardClick(game.key)}
            style={{ cursor: 'pointer' }}
          >
            <img 
              src={game.image_url || "/e.png"} 
              alt={game.name} 
              className="mini-game-img" 
            />
            <div className="mini-game-info">
              <h4>{game.key.replace(/_/g, ' ').replace(/\b\w/g, (char: string) => char.toUpperCase())}</h4>
              <div className="mini-game-details">
                <span className="mini-rating">{game.rating}</span>
                <span className="mini-price">
                  {game.availability === 1 ? 'Free' : 'Coming Soon'}
                </span>
              </div>
            </div>
          </div>
        ))}
      </div>
      <GameConfirmationModal
        isOpen={isModalOpen}
        onClose={handleModalClose}
        onConfirm={handleModalConfirm}
        gameTitle={featuredGame?.name || ''}
        gameKey={selectedGame || ''}
        description={featuredGame?.long_description || featuredGame?.short_description || ''}
        difficulty={DIFFICULTY_MAP[selectedDifficulty]}
        videoUrl={featuredGame?.intro_video_url}
        imageUrl={featuredGame?.image_url}
        backgroundImageUrl={featuredGame?.background_image_url}
      />
    </div>
  );
};

export default FeaturedGames; 